import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { callErrorMessage, validateCredential, type ValidationOutcome } from "@/lib/hooks/useConvertLink";

// Salvar credencial = RPC (segredo vai para o Vault) + chamada de teste na edge
// convert-link, que grava status/needs_reauth/last_error exibidos no card.

export interface SaveState {
  readonly saving: boolean;
  readonly status: string | null;
  readonly error: string | null;
}

export function validationMessage(outcome: ValidationOutcome): { status: string | null; error: string | null } {
  if (outcome.status === "valid") {
    return { status: "Credencial salva e testada com sucesso.", error: null };
  }
  if (outcome.status === "unchanged") {
    return { status: `Credencial salva. O teste não foi conclusivo agora: ${outcome.message ?? "tente mais tarde"}.`, error: null };
  }
  return { status: null, error: `Credencial salva, mas a loja recusou o teste: ${outcome.message ?? "confira os dados"}.` };
}

/** Roda o teste da credencial e devolve o feedback; teste indisponivel nao e erro. */
export async function validationFeedback(marketplace: string): Promise<{ status: string | null; error: string | null }> {
  try {
    return validationMessage(await validateCredential(marketplace));
  } catch {
    return { status: "Credencial salva. O teste automático ficou indisponível agora.", error: null };
  }
}

/** Mapeia o erro do RPC (RAISE EXCEPTION codigo) para texto pt-BR. */
export function rpcErrorMessage(message: string, labels: Readonly<Record<string, string>>): string {
  const code = Object.keys(labels).find((key) => message.includes(key));
  return code ? (labels[code] as string) : "Não foi possível salvar. Confira os campos e tente de novo.";
}

export function useCredentialSave(marketplace: string, errorLabels: Readonly<Record<string, string>>) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<SaveState>({ saving: false, status: null, error: null });

  const save = async (rpc: string, params: Record<string, unknown>): Promise<boolean> => {
    setState({ saving: true, status: null, error: null });
    const { error } = await supabase.rpc(rpc, params);
    if (error) {
      setState({ saving: false, status: null, error: rpcErrorMessage(error.message, errorLabels) });
      return false;
    }
    try {
      const outcome = await validateCredential(marketplace);
      setState({ saving: false, ...validationMessage(outcome) });
    } catch (validationError) {
      setState({ saving: false, status: `Credencial salva. Teste indisponível: ${callErrorMessage(validationError)}`, error: null });
    }
    await queryClient.invalidateQueries({ queryKey: ["affiliate-credentials"] });
    return true;
  };

  return { ...state, save };
}
